import { Injectable, computed, signal } from '@angular/core';

import { AuthUser, ExternalAuthProvider, LoginRequest, OidcClientConfig, RegisterRequest } from './auth.models';

const STORAGE_KEY = 'central-profile.auth-user';

const buildRedirectUrl = () => {
  const origin = typeof window === 'undefined' ? 'http://localhost:4200' : window.location.origin;
  return `${origin}/dashboard`;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly oidcConfig: OidcClientConfig = {
    authority: 'https://identity.centralprofile.local',
    clientId: 'central-profile-spa',
    scope: 'openid profile email offline_access centralprofile.api',
    responseType: 'code',
    redirectUrl: buildRedirectUrl(),
    postLogoutRedirectUri: typeof window === 'undefined' ? 'http://localhost:4200/login' : `${window.location.origin}/login`,
    silentRenew: true,
    useRefreshToken: true
  };

  private readonly userState = signal<AuthUser | null>(this.readStoredUser());
  readonly currentUser = computed(() => this.userState());
  readonly isAuthenticated = computed(() => !!this.userState());

  login(payload: LoginRequest): void {
    this.persistUser({
      fullName: payload.email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
      email: payload.email,
      phoneNumber: '+27 67 123 4567'
    });
  }

  register(payload: RegisterRequest): void {
    this.persistUser({
      fullName: payload.fullName,
      email: payload.email,
      phoneNumber: payload.phoneNumber
    });
  }

  requestPasswordReset(_email: string): void {}

  resetPassword(_password: string): void {}

  signInWithProvider(provider: ExternalAuthProvider): void {
    const providerNames: Record<ExternalAuthProvider, string> = {
      google: 'Google',
      facebook: 'Facebook',
      microsoft: 'Microsoft'
    };

    this.persistUser({
      fullName: `${providerNames[provider]} Connected User`,
      email: `${provider}@centralprofile.local`,
      phoneNumber: '+27 71 000 0000'
    });
  }

  logout(): void {
    this.userState.set(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }

  private persistUser(user: AuthUser): void {
    this.userState.set(user);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    }
  }

  private readStoredUser(): AuthUser | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    try {
      return JSON.parse(rawValue) as AuthUser;
    } catch {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }
}